import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';
import {
  AXIS_END,
  AXIS_START,
  AXIS_TICKS,
  NOW_YEAR,
  education,
  engineering,
  languages,
  openSource,
  passionProjects,
  profile,
  roots,
  skills,
  videos,
} from './profileData';

const AXIS_SPAN = AXIS_END - AXIS_START;
const pct = (year) => ((year - AXIS_START) / AXIS_SPAN) * 100;

function SectionHeading({ kicker, title }) {
  return (
    <div className="mb-7 md:mb-9">
      <div className="nb-meta mb-2 text-[11px] font-bold uppercase text-[color:var(--muted)]">{kicker}</div>
      <h2 className="text-2xl font-extrabold tracking-tight md:text-4xl">{title}</h2>
      <div className="nb-rule mt-4 w-full" />
    </div>
  );
}

function Hero() {
  return (
    <header className="pt-12 pb-14 md:pt-20 md:pb-20">
      <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
        <div className="home-monogram flex h-20 w-20 shrink-0 items-center justify-center text-3xl md:h-28 md:w-28 md:text-5xl">
          JV
        </div>
        <div className="min-w-0">
          <h1 className="text-[2.5rem] font-extrabold leading-[0.95] tracking-[-0.035em] md:text-[5rem]">
            {profile.name}
          </h1>
          <p className="mt-3 text-lg font-bold text-[color:var(--primary)] md:text-2xl">{profile.title}</p>
          <p className="nb-meta mt-2 text-xs font-semibold uppercase text-[color:var(--muted)] md:text-sm">
            {profile.location}
          </p>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-[color:var(--muted)] md:text-lg">
            {profile.tagline}
          </p>

          <div className="mt-7 flex flex-wrap gap-3">
            {profile.links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.href.startsWith('mailto:') ? undefined : '_blank'}
                rel={link.href.startsWith('mailto:') ? undefined : 'noreferrer'}
                className={`nb-btn px-4 py-2.5 text-sm md:text-base ${link.primary ? 'nb-btn--primary' : ''}`}>
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </header>
  );
}

function About() {
  return (
    <section className="pb-14 md:pb-20">
      <SectionHeading kicker="01 — About" title="Language first, then code" />
      <div className="max-w-3xl space-y-4">
        {profile.about.map((para) => (
          <p key={para.slice(0, 24)} className="text-base leading-relaxed md:text-lg">
            {para}
          </p>
        ))}
      </div>
    </section>
  );
}

/* Renders a note, turning the single word named by `link.text` into an anchor.
   Notes without a `noteLink` pass straight through unchanged. */
function NoteWithLink({ note, link }) {
  const at = link ? note.indexOf(link.text) : -1;
  if (at === -1) return note;
  return (
    <>
      {note.slice(0, at)}
      <a href={link.href} target="_blank" rel="noreferrer" className="nb-inline-link">
        {link.text}
      </a>
      {note.slice(at + link.text.length)}
    </>
  );
}

function Experience() {
  return (
    <section className="pb-14 md:pb-20">
      <SectionHeading kicker="02 — Experience" title="The engineering path" />

      <ol className="career-rail relative m-0 list-none p-0">
        {engineering.map((job) => (
          <li key={`${job.company}-${job.period}`} className="relative pb-9 pl-9 last:pb-0 md:pl-12">
            <span className={`career-node ${job.current ? 'career-node--current' : ''}`} />
            <div className="nb-meta text-xs font-bold uppercase text-[color:var(--muted)]">
              {job.period}
              {job.current && <span className="ml-2 text-[color:var(--primary)]">● now</span>}
            </div>
            <h3 className="mt-1.5 text-xl font-extrabold leading-tight tracking-tight md:text-3xl">{job.role}</h3>
            <div className="mt-1 text-base font-bold text-[color:var(--muted)] md:text-lg">{job.company}</div>
            <ul className="mt-3 list-none space-y-2 p-0">
              {job.bullets.map((bullet) => (
                <li key={bullet.slice(0, 24)} className="relative pl-5 text-sm leading-relaxed md:text-base">
                  <span className="absolute left-0 top-[0.5em] h-2 w-2 border-2 border-[color:var(--ink)] bg-white" />
                  {bullet}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>

      <h3 className="mt-14 text-lg font-extrabold uppercase tracking-[0.12em] md:text-xl">Where it started</h3>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[color:var(--muted)] md:text-base">
        Translating, interpreting, and teaching — the work that taught me how people actually use the systems we build.
      </p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {roots.map((role) => (
          <div key={`${role.role}-${role.period}`} className="nb-flat p-4 md:p-5">
            <div className="nb-meta text-[11px] font-bold uppercase text-[color:var(--muted)]">{role.period}</div>
            <div className="mt-1.5 text-base font-extrabold leading-snug md:text-lg">{role.role}</div>
            <div className="mt-0.5 text-sm font-bold text-[color:var(--muted)]">{role.company}</div>
            <p className="mt-2 text-sm leading-relaxed">
              <NoteWithLink note={role.note} link={role.noteLink} />
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Skills() {
  return (
    <section className="pb-14 md:pb-20">
      <SectionHeading kicker="03 — Toolkit" title="What I build with" />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {skills.map((bucket) => (
          <div key={bucket.group}>
            <div className="nb-meta mb-3 text-[11px] font-bold uppercase text-[color:var(--muted)]">
              {bucket.group}
            </div>
            <div className="flex flex-wrap gap-2">
              {bucket.items.map((item) => (
                <span key={item} className="nb-chip px-2.5 py-1.5 text-xs md:text-sm">
                  {item}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function LanguageTrack({ language }) {
  const ongoing = language.end === null;
  const end = ongoing ? NOW_YEAR : language.end;
  const left = pct(language.start);
  const width = Math.max(pct(end) - left, 0);

  return (
    <div className="lang-track">
      {AXIS_TICKS.map((tick) => (
        <span key={tick} className="lang-gridline" style={{ left: `${pct(tick)}%` }} />
      ))}
      <span
        className={`lang-bar ${ongoing ? 'lang-bar--ongoing' : ''}`}
        style={{ left: `${left}%`, width: `${width}%`, background: language.color }}>
        {ongoing && <span className="lang-live-dot" />}
      </span>
    </div>
  );
}

function LanguageRow({ language, open, onToggle }) {
  const hasStory = Boolean(language.story);
  const storyId = `lang-story-${language.name.toLowerCase()}`;
  const caret = hasStory ? <span className={`lang-caret ${open ? 'lang-caret--open' : ''}`} /> : null;

  /* With a story the whole row is one button, so name, level and bar are a
     single click target and a single tab stop. Without one it is inert. */
  const Row = hasStory ? 'button' : 'div';
  const rowProps = hasStory
    ? { type: 'button', onClick: onToggle, 'aria-expanded': open, 'aria-controls': storyId }
    : {};

  return (
    <li className="relative border-t-2 border-dotted border-[rgba(11,11,15,0.18)] py-3 first:border-t-0 first:pt-0">
      <Row
        className="lang-row block md:grid md:grid-cols-[9.5rem_1fr_6.5rem] md:items-center md:gap-4"
        {...rowProps}>
        <div className="mb-2 flex items-baseline justify-between gap-3 md:mb-0 md:block">
          <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 md:block">
            <span className="lang-name text-base md:text-lg">{language.name}</span>
            <span className="lang-level md:mt-0.5 md:block">{language.level}</span>
          </span>
          <span className="lang-years md:hidden">
            {language.years}
            {caret}
          </span>
        </div>

        <LanguageTrack language={language} />

        <div className="lang-years hidden text-right md:block">
          {language.years}
          {caret}
        </div>
      </Row>

      {hasStory && open && (
        <div id={storyId} className="lang-popover p-4 md:p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="lang-name text-base md:text-lg">{language.name}</span>
              <span className="lang-years ml-2">{language.years}</span>
            </div>
            <button
              type="button"
              onClick={onToggle}
              aria-label={`Close the ${language.name} story`}
              className="lang-popover-close h-6 w-6 shrink-0">
              ×
            </button>
          </div>
          <p className="mt-2 text-sm leading-relaxed md:text-base">{language.story}</p>
        </div>
      )}
    </li>
  );
}

function Languages() {
  const [openName, setOpenName] = useState(null);
  const chartRef = useRef(null);

  /* Dismiss the popover on a click outside the chart or on Escape. */
  useEffect(() => {
    if (!openName) return undefined;
    const onPointerDown = (event) => {
      if (chartRef.current && !chartRef.current.contains(event.target)) setOpenName(null);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setOpenName(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [openName]);

  return (
    <section className="pb-14 md:pb-20">
      <SectionHeading kicker="04 — Languages" title="Nine languages, thirty years" />

      <div className="nb-card p-4 md:p-7" ref={chartRef}>
        {/* Decade axis. Hidden from assistive tech — every row states its own years. */}
        <div className="mb-3 md:grid md:grid-cols-[9.5rem_1fr_6.5rem] md:gap-4" aria-hidden="true">
          <div className="hidden md:block" />
          <div className="relative h-4">
            {AXIS_TICKS.map((tick) => (
              <span
                key={tick}
                className="nb-meta absolute top-0 text-[10px] font-bold text-[color:var(--muted)]"
                style={{ left: `${pct(tick)}%` }}>
                {tick}
              </span>
            ))}
          </div>
          <div className="hidden md:block" />
        </div>

        <ul className="m-0 list-none p-0">
          {languages.map((language) => (
            <LanguageRow
              key={language.name}
              language={language}
              open={openName === language.name}
              onToggle={() => setOpenName((current) => (current === language.name ? null : language.name))}
            />
          ))}
        </ul>
      </div>

      <p className="mt-5 text-sm leading-relaxed text-[color:var(--muted)] md:text-base">
        Rows with a <span className="lang-caret ml-0 mr-1" /> have a story behind them — click one to read it. More to
        come.
      </p>
    </section>
  );
}

function Travel() {
  return (
    <section className="pb-14 md:pb-20">
      <SectionHeading kicker="05 — Travel" title="Places, on video" />
      <div className="grid gap-6 md:grid-cols-2 md:gap-8">
        {videos.map((video) => (
          <figure key={video.id} className="nb-card m-0 overflow-hidden">
            <div className="video-frame">
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${video.id}`}
                title={video.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <figcaption className="border-t-[3px] border-[color:var(--ink)] p-4 md:p-5">
              <div className="text-base font-extrabold leading-snug md:text-lg">{video.title}</div>
              <div className="mt-1 text-sm text-[color:var(--muted)]">{video.note}</div>
            </figcaption>
          </figure>
        ))}
      </div>
    </section>
  );
}

function Projects() {
  return (
    <section className="pb-14 md:pb-20">
      <SectionHeading kicker="06 — Projects" title="Things I've made" />

      <div className="flex flex-col gap-5">
        {passionProjects.map((project) => (
          <div key={project.name} className="nb-card p-5 md:p-7">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-8">
              <div className="min-w-0">
                {project.kicker && (
                  <div className="nb-meta mb-2 text-[11px] font-bold uppercase text-[color:var(--primary)]">
                    {project.kicker}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-extrabold tracking-tight md:text-2xl">{project.name}</h3>
                  {project.status && (
                    <span className="nb-chip px-2 py-1 text-[10px] uppercase tracking-[0.1em]">{project.status}</span>
                  )}
                </div>
                <p className="mt-2 max-w-xl text-sm leading-relaxed md:text-base">{project.note}</p>
              </div>
              <div className="shrink-0">
                {project.to ? (
                  <Link
                    to={project.to}
                    className={`nb-btn px-4 py-2.5 text-sm md:text-base ${project.featured ? 'nb-btn--primary' : ''}`}>
                    Visit →
                  </Link>
                ) : (
                  <a
                    href={project.href}
                    target="_blank"
                    rel="noreferrer"
                    className={`nb-btn px-4 py-2.5 text-sm md:text-base ${project.featured ? 'nb-btn--primary' : ''}`}>
                    Visit →
                  </a>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mt-12 text-lg font-extrabold uppercase tracking-[0.12em] md:text-xl">Open source & tools</h3>
      <div className="mt-6 grid gap-5 md:grid-cols-3">
        {openSource.map((project) => (
          <div key={project.name} className="nb-flat flex flex-col p-5">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-lg font-extrabold tracking-tight">{project.name}</h4>
              {project.status && (
                <span className="nb-chip px-2 py-0.5 text-[10px] uppercase tracking-[0.1em]">{project.status}</span>
              )}
            </div>
            <p className="mt-2 flex-1 text-sm leading-relaxed">{project.note}</p>
            <a
              href={project.href}
              target="_blank"
              rel="noreferrer"
              className="nb-btn mt-4 self-start px-3 py-2 text-xs md:text-sm">
              {project.hrefLabel} →
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

function Education() {
  return (
    <section className="pb-12">
      <div className="nb-meta mb-2 text-[11px] font-bold uppercase text-[color:var(--muted)]">Education</div>
      <p className="max-w-3xl text-sm leading-relaxed text-[color:var(--muted)] md:text-base">
        <span className="font-bold text-[color:var(--ink)]">{education.degree}</span>
        {` · ${education.school} · ${education.extra} · ${education.period}`}
      </p>
    </section>
  );
}

function Home() {
  useEffect(() => {
    document.body.classList.add('home-page');
    const previousTitle = document.title;
    document.title = `${profile.name} — ${profile.title}`;
    return () => {
      document.body.classList.remove('home-page');
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="home min-h-screen">
      <div className="mx-auto w-full max-w-5xl px-5 md:px-8">
        <Hero />
        <About />
        <Experience />
        <Skills />
        <Languages />
        <Travel />
        <Projects />
        <Education />

        <div className="nb-rule w-full" />
        <footer className="flex flex-col gap-3 py-8 text-xs font-bold uppercase tracking-[0.15em] text-[color:var(--muted)] md:flex-row md:items-center md:justify-between">
          <span>javascriptorian.com</span>
          <a href="mailto:jesse@langcharm.com" className="font-bold text-[color:var(--ink)] no-underline hover:underline">
            jesse@langcharm.com
          </a>
        </footer>
      </div>
    </div>
  );
}

export default Home;
